import { AnimatedSection } from "@/components/common/AnimatedSection"
import { SectionLabel } from "@/components/common/SectionLabel"
import { Button } from "@/components/ui/Button"
import { useSEO } from "@/hooks/useSEO"
import { CTASection } from "@/sections/CTASection"
import { createFileRoute, Link } from "@tanstack/react-router"
import { motion } from "framer-motion"
import {
  Activity,
  ArrowRight,
  BarChart3,
  CreditCard,
  DollarSign,
  Globe,
  Lock,
  Users,
  Zap,
} from "lucide-react"

export const Route = createFileRoute("/features/")({
  component: FeaturesPage,
})

const ALL_FEATURES = [
  {
    icon: Users,
    title: "Merchant Onboarding",
    description:
      "Streamlined KYC and certification flows that get merchants live fast. Includes document collection, automated screening, and continuous monitoring.",
    details: [
      "Automated KYC/AML checks",
      "Document collection portal",
      "Risk scoring engine",
      "Ongoing transaction monitoring",
    ],
    color: "bg-blue-light",
    iconColor: "text-blue",
  },
  {
    icon: CreditCard,
    title: "Transaction Processing",
    description:
      "Cards, bank transfers, and wallets in one API. Precise and comprehensive, designed for high throughput and reliability at any scale.",
    details: [
      "Visa, Mastercard, Amex, Discover",
      "SEPA & ACH bank transfers",
      "Apple Pay, Google Pay",
      "Local payment methods globally",
    ],
    color: "bg-blue-light",
    iconColor: "text-blue",
  },
  {
    icon: DollarSign,
    title: "Settlement Engine",
    description: "Automated splits and payouts with configurable schedules, currencies, and fees.",
    details: [
      "Multi-currency settlements",
      "Configurable split rules",
      "Holdback management",
      "Automated reconciliation",
    ],
    color: "bg-green-light",
    iconColor: "text-green-DEFAULT",
  },
  {
    icon: Activity,
    title: "Analytics Dashboard",
    description: "Real-time metrics, reconciliation, and exportable reports.",
    details: [
      "Real-time transaction feed",
      "Drill-down reporting",
      "CSV & API exports",
      "Custom metric dashboards",
    ],
    color: "bg-amber-light",
    iconColor: "text-amber-DEFAULT",
  },
  {
    icon: Lock,
    title: "Fraud Prevention",
    description: "ML-powered scoring, 3D Secure authentication, and velocity controls.",
    details: [
      "ML fraud scoring",
      "3D Secure 2.0",
      "Velocity rules engine",
      "Chargeback management",
    ],
    color: "bg-rose-light",
    iconColor: "text-rose-DEFAULT",
  },
  {
    icon: Zap,
    title: "Webhooks & Events",
    description: "Real-time event streaming for every payment lifecycle event.",
    details: [
      "Sub-second delivery",
      "Retry with backoff",
      "Event filtering",
      "HMAC signature verification",
    ],
    color: "bg-blue-light",
    iconColor: "text-blue",
  },
  {
    icon: Globe,
    title: "Global Routing",
    description: "Intelligent payment routing across multiple acquirers and regions.",
    details: [
      "Smart failover routing",
      "Cost-optimized paths",
      "Multi-acquirer support",
      "Country-specific rules",
    ],
    color: "bg-green-light",
    iconColor: "text-green-DEFAULT",
  },
  {
    icon: BarChart3,
    title: "Reconciliation",
    description: "Automated reconciliation between your books and payment processor data.",
    details: [
      "Daily auto-reconciliation",
      "Exception flagging",
      "Ledger integration",
      "Dispute management",
    ],
    color: "bg-amber-light",
    iconColor: "text-amber-DEFAULT",
  },
]

function FeaturesPage() {
  useSEO({
    title: "Features",
    description:
      "Explore Orchestrate's full feature set: merchant onboarding, transaction processing, settlement engine, fraud prevention, analytics, and more.",
    keywords:
      "payment features, merchant onboarding, settlement engine, fraud prevention, payment analytics",
    canonical: "https://orchestrate.global/features",
  })

  return (
    <>
      {/* Hero */}
      <section className="bg-surface grid-bg relative overflow-hidden pt-32 pb-20">
        <div className="hero-glow" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-6 text-center lg:px-8">
          <AnimatedSection>
            <SectionLabel label="Platform capabilities" centered className="mb-5" />
          </AnimatedSection>
          <AnimatedSection delay={0.1}>
            <h1 className="font-display mb-6 text-5xl leading-tight tracking-tight sm:text-6xl lg:text-7xl">
              Built for every
              <br />
              <em className="text-blue not-italic">payment complexity.</em>
            </h1>
          </AnimatedSection>
          <AnimatedSection delay={0.2}>
            <p className="text-muted mx-auto mb-10 max-w-2xl text-xl">
              One unified API to handle every aspect of your payment stack — from onboarding to
              reconciliation.
            </p>
            <Button variant="primary" size="lg" asChild>
              <Link to="/contact">
                Start building <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </AnimatedSection>
        </div>
      </section>

      {/* Features grid */}
      <section className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {ALL_FEATURES.map((feature, i) => {
              const Icon = feature.icon
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: (i % 4) * 0.1, ease: [0.16, 1, 0.3, 1] }}
                  viewport={{ once: true, amount: 0.08 }}
                  className="border-border hover:shadow-ink/8 group cursor-default rounded-2xl border bg-white p-6 transition-all duration-250 hover:-translate-y-1.5 hover:shadow-xl"
                >
                  <div
                    className={`h-11 w-11 ${feature.color} mb-5 flex items-center justify-center rounded-xl transition-transform duration-200 group-hover:scale-110`}
                  >
                    <Icon className={`h-5 w-5 ${feature.iconColor}`} />
                  </div>
                  <h3 className="mb-2 text-base font-semibold">{feature.title}</h3>
                  <p className="text-muted mb-4 text-sm leading-relaxed">{feature.description}</p>
                  <ul className="space-y-1.5">
                    {feature.details.map((detail) => (
                      <li key={detail} className="text-muted flex items-center gap-2 text-xs">
                        <div
                          className="bg-blue h-1 w-1 flex-shrink-0 rounded-full"
                          aria-hidden="true"
                        />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  )
}
